import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

interface NewProjectDialogProps {
    open: boolean;
    onClose: () => void;
    onCreate: (name: string, description: string) => Promise<void>;
}

export const NewProjectDialog: React.FC<NewProjectDialogProps> = ({ open, onClose, onCreate }) => {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const reset = () => {
        setName('');
        setDescription('');
        setSubmitting(false);
    };

    const handleCreate = async () => {
        if (!name.trim() || submitting) return;
        setSubmitting(true);
        try {
            await onCreate(name.trim(), description.trim());
            reset();
            onClose();
        } catch {
            setSubmitting(false);
        }
    };

    const handleCancel = () => {
        reset();
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={(next) => { if (!next) handleCancel(); }}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{t('CONTENT_STUDIO.NEW_PROJECT_TITLE', 'New project')}</DialogTitle>
                </DialogHeader>

                <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor="cs-new-project-name" className="text-muted-foreground">
                            {t('CONTENT_STUDIO.PROJECT_NAME', 'Project name')}
                        </Label>
                        <Input
                            id="cs-new-project-name"
                            autoFocus
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder={t('CONTENT_STUDIO.PROJECT_NAME_PLACEHOLDER', 'e.g. Spring 2026 Launch') || ''}
                        />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <Label htmlFor="cs-new-project-description" className="text-muted-foreground">
                            {t('CONTENT_STUDIO.PROJECT_DESCRIPTION', 'Description (optional)')}
                        </Label>
                        <Textarea
                            id="cs-new-project-description"
                            rows={3}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={handleCancel}>
                        {t('CONTENT_STUDIO.CANCEL', 'Cancel')}
                    </Button>
                    <Button variant="outline" onClick={handleCreate} disabled={!name.trim() || submitting}>
                        {submitting
                            ? t('CONTENT_STUDIO.CREATING', 'Creating…')
                            : t('CONTENT_STUDIO.CREATE_PROJECT', 'Create project')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
